<template>
    <asli-panel background="bg-panel-tertiary" class="mt-7" show-left-border>
        <div class="hidden">
            <asli-address-search id="hidden-address-search" ref="addressSearchRef" v-model="addressModel" />
        </div>
        <summary-section
            title="Your Details"
            section-name="details"
            :fields="personalDetailsFields"
            :data="personalDetails"
            @navigate="navigateToSection"
            :editing-disabled="editingDisabled"
        />
        <summary-section
            title="Your Property"
            section-name="property"
            :fields="propertyDetailsFields"
            :data="propertyDetailsWithUpdatedAddress"
            @navigate="navigateToSection"
            :editing-disabled="editingDisabled"
        />
        <summary-section
            title="Your Cover"
            section-name="cover"
            :fields="coverDetailsFields"
            :data="coverDetails"
            @navigate="navigateToSection"
            :editing-disabled="editingDisabled"
        />
    </asli-panel>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useMainStore } from "@/stores/main";
import { useProposalStore } from "@/stores/risk/risk";
import { FORM_MODE, ROUTE_NAMES } from "@/router/types";
import { formatDate, sanitizeCoverDetails } from "@/utils";
import { FormRouteName } from "@/router/utils";
import { AsliPanel, AsliAddressSearch, toCurrency, printAddress } from "@mbs-npm/anpost-ui";
import SummarySection from "./SummarySection.vue";
import { getListValueByName } from "@/hooks/getListValueName";
import dayjs from "dayjs";
import { AlarmType } from "@/stores/lists/types";

const props = defineProps({
    payMonthly: {
        type: Boolean,
        required: true
    }
});

const router = useRouter();
const mainStore = useMainStore();
const proposalStore = useProposalStore();
const initialPayMonthly = proposalStore.proposal.is_pay_monthly;
const storedRisk = proposalStore.proposal.risk;
const editingDisabled = ref<boolean>(proposalStore.proposal.recalled_from_offline);
const addressSearchRef = ref<InstanceType<typeof AsliAddressSearch> | null>(null);
const addressModel = ref(storedRisk.property_details.address);
const risk = computed(() => proposalStore.proposal.risk);

function getSportsEquipmentAmount(coverDetails) {
    if (coverDetails.sports_equipment_amount != null) {
        const strValue = coverDetails.sports_equipment_amount.toString().trim();
        if (strValue) return parseInt(strValue.replace(/,/g, ""));
    }
    return null;
}

async function setPaymentChoice() {
    if (props.payMonthly !== initialPayMonthly) {
        mainStore.setIsMonthly(props.payMonthly);
        await proposalStore.UpdatePaymentChoice(props.payMonthly);
    }
}

const navigateToSection = (section: string) => {
    const coverDetails = risk.value.cover_details;
    coverDetails.sports_equipment_amount = getSportsEquipmentAmount(coverDetails);
    if (!risk.value.interested_parties.length) {
        proposalStore.patchInterestedParties(false);
    }
    const updatedCoverDetails = sanitizeCoverDetails(coverDetails);
    if (updatedCoverDetails !== coverDetails) proposalStore.patchCoverDetails(updatedCoverDetails);
    setPaymentChoice();
    const routeMap = {
        details: ROUTE_NAMES.AboutYou,
        property: ROUTE_NAMES.YourProperty,
        cover: ROUTE_NAMES.YourCover
    };

    router.push({
        name: FormRouteName(routeMap[section], FORM_MODE.EDIT)
    });
};

const personalDetailsFields = [
    { label: "First Name", key: "firstName" },
    { label: "Last Name", key: "lastName" },
    { label: "Date of Birth", key: "dateOfBirth" },
    { label: "Mobile", key: "mobile" },
    { label: "Email", key: "email" },
    { label: "Occupation", key: "occupation" }
];

const propertyDetailsFields = [
    { label: "Address", key: "address" },
    { label: "Property Type", key: "propertyType" },
    { label: "Year Built", key: "yearBuilt" },
    { label: "Bedrooms", key: "bedrooms" },
    { label: "Bathrooms", key: "bathrooms" },
    { label: "Alarm", key: "alarm" },
    { label: "Heating Type", key: "heatingType" }
];

const coverDetailsFields = [
    { label: "Claims Free Years", key: "claimsFreeYears" },
    { label: "Number of Claims", key: "numberOfClaims" },
    { label: "Buildings Cover", key: "buildingsCover", condition: "buildingsCover", isCurrency: true },
    { label: "Contents Cover", key: "contentsCover", condition: "contentsCover", isCurrency: true },
    { label: "Policy Start Date", key: "policyStartDate" },
    { label: "Joint Policyholder", key: "hasJointPolicyHolder" }
];

const personalDetails = computed(() => ({
    title: storedRisk.proposer.personal_details.title,
    firstName: storedRisk.proposer.personal_details.first_name,
    lastName: storedRisk.proposer.personal_details.surname,
    dateOfBirth: formatDate(storedRisk.proposer.personal_details.date_of_birth),
    mobile: storedRisk.proposer.mobile_phone_number || "None",
    email: storedRisk.proposer.email || "None",
    occupation: getListValueByName("occupation", storedRisk.proposer.employment_details.occupation)
}));

const propertyDetails = computed(() => {
    const property = storedRisk.property_details;
    return {
        address: printAddress(property.address),
        propertyType: getListValueByName("property_type", property.type) ?? "",
        yearBuilt: dayjs(property.year_built).year(),
        bedrooms: property.number_of_bedrooms,
        bathrooms: property.number_of_bathrooms,
        alarm: property.security_details.alarm_type !== AlarmType.None ? "Yes" : "No",
        heatingType: property.heating_system
    };
});

const coverDetails = computed(() => {
    const previousPolicy = storedRisk.proposer.previous_policy_details;
    return {
        claimsFreeYears: previousPolicy?.no_claims_discount_years || 0,
        numberOfClaims: previousPolicy?.has_claims ? previousPolicy?.claims.length : 0,
        buildingsCover: toCurrency(storedRisk.cover_details.buildings_cover_amount),
        contentsCover: toCurrency(storedRisk.cover_details.contents_cover_amount),
        policyStartDate: formatDate(storedRisk.policy_details.start_date),
        hasJointPolicyHolder: storedRisk.has_joint_proposers ? "Yes" : "No"
    };
});

const propertyDetailsWithUpdatedAddress = computed(() => {
    const details = { ...propertyDetails.value };

    if (addressModel.value.address_line1) {
        details.address = printAddress(addressModel.value);
    }

    return details;
});

watch(addressModel, newAddress => {
    if (newAddress.address_line1) {
        proposalStore.patchAddress(newAddress);
    }
});

onMounted(async () => {
    if (storedRisk.property_details.address.eircode && !storedRisk.property_details.address.address_line1) {
        await nextTick();
        addressSearchRef.value?.findAddress();
    }
});
</script>
